import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { MessageSquare, RefreshCw } from 'lucide-react'

interface Conversation {
  id: string
  channelKind: string
  externalThreadId: string
  status: string
  lastMessageAt: string
  lastMessagePreview: string | null
  unreadCount: number
  messages: { id: string; body: string }[]
}

export function InboxPage() {
  const [items, setItems] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setErr(null)
    try {
      const data = await api<{ items: Conversation[] }>('/v1/marketing/inbox/conversations')
      setItems(data.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  return (
    <div className="p-6 max-w-5xl">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight">Inbox omnicanal</h1>
          <p className="text-xs font-mono text-ink-700/60">
            F10 Sprint 0 · canales en modo mock · usa el simulador para inyectar mensajes
          </p>
        </div>
        <button className="bp-btn" onClick={() => void load()} disabled={loading}>
          <RefreshCw className="size-3.5" />
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </header>

      {err && (
        <div className="bp-card p-3 text-sm font-mono text-red-700 border-red-300 bg-red-50">
          {err}
        </div>
      )}

      {!err && items.length === 0 && !loading && (
        <div className="bp-card p-8 text-center text-sm text-ink-700/60">
          <MessageSquare className="size-6 mx-auto mb-2 opacity-50" />
          No hay conversaciones todavía. Abre el{' '}
          <a className="text-blueprint-600 underline" href="/marketing/simulator">
            Simulador
          </a>{' '}
          para crear una.
        </div>
      )}

      <ul className="space-y-1">
        {items.map((c) => (
          <li key={c.id}>
            <Link
              to={`/marketing/inbox/${c.id}`}
              className="bp-card px-3 py-2 flex items-center gap-3 hover:bg-surface-alt"
            >
              <span className="bp-pill">{c.channelKind}</span>
              <span className="font-mono text-xs text-ink-700/60 shrink-0">
                {c.externalThreadId}
              </span>
              <span className="text-sm text-ink-900 truncate flex-1">
                {c.lastMessagePreview ?? '(sin mensajes)'}
              </span>
              {c.unreadCount > 0 && (
                <span className="bp-pill bg-blueprint-600 text-white">{c.unreadCount}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
